import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import { Calendar, FileText, CreditCard, X, Folder, List, Loader2 } from 'lucide-react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useAuth } from '../../../context/AuthContext';
import { db } from '../../../firebase';

const CostModal = ({
  isOpen,
  onClose,
  costForm,
  setCostForm,
  onSave,
  isEditing,
  isSaving = false,
}) => {
  const { t } = useTranslation('home');
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availableSubCategories, setAvailableSubCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      try {
        const snap = await getDocs(
          query(collection(db, 'cost_categories'), where('businessId', '==', user.businessId))
        );
        const categoriesList = snap.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(cat => cat.status === 'active')
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setCategories(categoriesList);
        if (isEditing && costForm.categoryId) {
          const sel = categoriesList.find(cat => cat.id === costForm.categoryId);
          if (sel) setAvailableSubCategories(sel.subCategories || []);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
      } finally {
        setLoading(false);
      }
    };
    if (isOpen) fetchCategories();
  }, [isOpen, isEditing, costForm.categoryId, user.businessId]);

  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    const sel = categories.find(cat => cat.id === categoryId);
    setCostForm(prev => ({ ...prev, categoryId, categoryName: sel ? sel.name : '', subCategory: '' }));
    setAvailableSubCategories(sel ? (sel.subCategories || []) : []);
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={{
        overlay: { backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
        content: { position: 'relative', top: 'auto', left: 'auto', right: 'auto', bottom: 'auto', maxWidth: '600px', width: '90%', maxHeight: '90vh', margin: '0 auto', padding: 0, border: 'none', background: 'transparent', overflow: 'visible' },
      }}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-red-500 to-pink-500 p-4 rounded-t-lg flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white">
            {isEditing ? t('costModal.titleEdit') : t('costModal.titleNew')}
          </h2>
          <button onClick={onClose} disabled={isSaving} className="text-white hover:text-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Calendar className="h-4 w-4 text-red-500" />
              {t('costModal.dateLabel')}
            </label>
            <input type="date" value={costForm.date}
              onChange={(e) => setCostForm(prev => ({ ...prev, date: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all" />
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Folder className="h-4 w-4 text-red-500" />
              {t('costModal.categoryLabel')}
            </label>
            <select value={costForm.categoryId || ''} onChange={handleCategoryChange} disabled={loading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all">
              <option value="">{t('costModal.categoryPlaceholder')}</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </div>

          {costForm.categoryId && (
            <div className="space-y-2 animate-fadeIn">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <List className="h-4 w-4 text-red-500" />
                {t('costModal.subCategoryLabel')}
              </label>
              <select value={costForm.subCategory || ''}
                onChange={(e) => setCostForm(prev => ({ ...prev, subCategory: e.target.value }))}
                disabled={availableSubCategories.length === 0}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all">
                <option value="">{t('costModal.subCategoryPlaceholder')}</option>
                {availableSubCategories.map((subCat, index) => (
                  <option key={index} value={subCat}>{subCat}</option>
                ))}
              </select>
              {availableSubCategories.length === 0 && (
                <p className="text-xs text-gray-500 italic">{t('costModal.noSubCategories')}</p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <FileText className="h-4 w-4 text-red-500" />
              {t('costModal.descriptionLabel')}
            </label>
            <input type="text" value={costForm.description || ''}
              onChange={(e) => setCostForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder={t('costModal.descriptionPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all" />
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <FileText className="h-4 w-4 text-red-500" />
              {t('costModal.amountLabel')}
            </label>
            <div className="relative">
              <input type="number" value={costForm.price}
                onChange={(e) => setCostForm(prev => ({ ...prev, price: e.target.value }))}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">TL</span>
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <CreditCard className="h-4 w-4 text-red-500" />
              {t('costModal.paymentTypeLabel')}
            </label>
            <select value={costForm.paymentType}
              onChange={(e) => setCostForm(prev => ({ ...prev, paymentType: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all">
              <option value="Nakit">{t('paymentTypes.Nakit')}</option>
              <option value="Kredi Kartı">{t('paymentTypes.Kredi Kartı')}</option>
              <option value="Havale">{t('paymentTypes.Havale')}</option>
            </select>
          </div>

          <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-lg p-4">
            <div className="text-lg font-medium flex items-center justify-between">
              <span>{t('costModal.totalLabel')}</span>
              <span>{costForm.price || 0} TL</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end gap-3">
          <button onClick={onClose} disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {t('costModal.cancel')}
          </button>
          <button onClick={onSave} disabled={isSaving || !costForm.categoryId || !costForm.date || !costForm.price}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 rounded-md transition-all disabled:opacity-70 disabled:cursor-not-allowed">
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            {isSaving ? t('costModal.saving') : (isEditing ? t('costModal.update') : t('costModal.save'))}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CostModal;
